import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, FormGroup } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import InputField from "../Utils/InputField";
import SelectField from "../Utils/SelectField";
import CommonButton from "../Utils/CommonButton";
import { LabelText } from "../Utils/Input";
import { Loader } from "../Utils/Loader";

import { getItems } from "../../slices/Item/GetItem/thunk";
import { getAllLocation } from "../../slices/Location/GetLocation/thunk";
import { createSO } from "../../slices/SO/CreateSO/thunk";

function InventorySOForm({ closeModal, selectedRow }) {
  const dispatch = useDispatch();

  const { items } = useSelector((state) => state.GetItems || {});
  const { locations = [] } = useSelector((state) => state.locations);

  const [loading, setLoading] = useState(false);
  const [apiloading, setApiLoading] = useState(false);
  const currencySymbol = localStorage.getItem("currencySymbol");

  useEffect(() => {
    dispatch(getItems(10000, 1));
    dispatch(getAllLocation(setApiLoading));
  }, [dispatch]);

  const itemOptions = useMemo(() => {
    return (
      items?.items?.map((item) => ({
        label: item.itemname,
        value: item.itemid,
        data: item,
      })) || []
    );
  }, [items]);

  useEffect(() => {
    if (!selectedRow) return;

    const matchedItem = itemOptions.find((i) => i.value === selectedRow.itemid);

    if (matchedItem) {
      handleItemChange(matchedItem);
    }
  }, [selectedRow, itemOptions]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      location: null,
      item: null,

      sku: "",
      unitPrice: 0,
      netQty: 0,
      orderQty: "",
      total: 0,
    },

    validationSchema: Yup.object({
      location: Yup.object().required("Required"),
      item: Yup.object().required("Required"),
      orderQty: Yup.number().required("Required").min(1),
    }),

    onSubmit: (values) => {
      const payload = {
        fromlocation: values.location?.value,
        soitems: [
          {
            itemid: values.item?.value,
            itemname: values.item?.label,
            sku: values.sku,
            orderedquantity: Number(values.orderQty),
            unitprice: Number(values.unitPrice),
            totalprice: Number(values.total),
          },
        ],
      };

      dispatch(
        createSO(payload, setLoading, () => {
          //   onSuccess?.();
          closeModal?.();
        }),
      );
    },
  });

  const { values, setFieldValue, errors, touched, handleSubmit, handleChange } =
    formik;
  const handleItemChange = (selected) => {
    if (!selected) return;

    const item = selected.data;

    setFieldValue("item", selected);
    setFieldValue("sku", item.sku || "");
    setFieldValue("unitPrice", item.unitprice || 0);
    setFieldValue("netQty", item.itemcount || 0);
    setFieldValue("orderQty", "");
    setFieldValue("total", 0);
  };

  useEffect(() => {
    const total =
      (Number(values.orderQty) || 0) * (Number(values.unitPrice) || 0);

    setFieldValue("total", total);
  }, [values.orderQty, values.unitPrice]);

  return (
    <form onSubmit={handleSubmit}>
      <Row className="gy-3">
        {loading && <Loader />}

        {/* LOCATION */}
        <Col md={12}>
          <FormGroup>
            <LabelText
              displayText="Location"
              important="*"
              className="text-danger"
            />

            <SelectField
              value={values.location}
              placeholder="Select Location"
              options={
                locations.map((l) => ({
                  label: l.name,
                  value: l.locationid,
                })) || []
              }
              handleChange={(val) => setFieldValue("location", val)}
              className={
                touched.location && errors.location ? "error-input" : ""
              }
            />
          </FormGroup>
        </Col>

        {/* ITEM SELECT */}
        <Col md={6}>
          <FormGroup>
            <LabelText
              displayText="Item Name"
              important="*"
              className="text-danger"
            />

            <SelectField
              value={values.item}
              options={itemOptions}
              isDisabled
              handleChange={handleItemChange}
            />
          </FormGroup>
        </Col>

        {/* SKU */}
        <Col md={6}>
          <FormGroup>
            <LabelText
              displayText="SKU"
              important="*"
              className="text-danger"
            />
            <InputField value={values.sku} disabled />
          </FormGroup>
        </Col>

        {/* NET QTY */}
        <Col md={6}>
          <FormGroup>
            <LabelText
              displayText="Net Qty"
              important="*"
              className="text-danger"
            />
            <InputField value={values.netQty} disabled />
          </FormGroup>
        </Col>

        {/* ORDER QTY */}
        <Col md={6}>
          <FormGroup>
            <LabelText
              displayText="Order Qty"
              important="*"
              className="text-danger"
            />
            <InputField
              name="orderQty"
              type="number"
              value={values.orderQty}
              handleChange={handleChange}
              className={
                touched.orderQty && errors.orderQty ? "error-input" : ""
              }
            />
          </FormGroup>
        </Col>

        {/* UNIT PRICE */}
        <Col md={6}>
          <FormGroup>
            <LabelText
              displayText={`Unit Price (${currencySymbol})`}
              important="*"
              className="text-danger"
            />
            <InputField value={values.unitPrice} disabled />
          </FormGroup>
        </Col>

        {/* TOTAL */}
        <Col md={6}>
          <FormGroup>
            <LabelText
              displayText={`Total (${currencySymbol})`}
              important="*"
              className="text-danger"
            />
            <InputField value={values.total} disabled />
          </FormGroup>
        </Col>

        {/* BUTTONS */}
        <div className="d-flex justify-content-center w-100 gap-2 flex-wrap pe-1 pb-3 mt-4">
          <div className="d-flex gap-2 justify-content-center">
            <CommonButton
              outline={true}
              className={
                "btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
              }
              color={"main-color"}
              type={"button"}
              buttonAction={() => {
                closeModal();
              }}
              buttonText={"Cancel"}
              icon={
                <i className="ri-close-fill label-icon secondary-label-icon align-middle fs-16 me-2"></i>
              }
              btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
            />

            <CommonButton
              color={"main-color"}
              className={
                "btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
              }
              icon={
                <i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-2"></i>
              }
              btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
              type={"submit"}
              buttonText={"Submit"}
            />
          </div>
        </div>
      </Row>
    </form>
  );
}

export default InventorySOForm;
